import {
  CalendarTimeRangeConfig,
  FixedTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCalendarTimeRanges,
  parseDefaultIntervals,
} from './CalendarTimeRange';
import { changeHourToEndOfDay, changeHourToStartOfDay } from './date';
import DateTimeRangePickerComponent from './DateTimeRangePicker';
import {
  formatCalendarDivisionPreview,
  inferDivideIntoSubintervals,
} from './divisionPreview';

type CalendarTimeRangeProps = {
  onChange: (range: (CalendarTimeRangeConfig | FixedTimeRangeConfig)[]) => void;
  noDivision?: boolean;
  defaultIntervals?: (CalendarTimeRangeConfig | FixedTimeRangeConfig)[];
};

type CalendarUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

function normalizeUnitForCalendarMode(
  unit: CalendarUnit,
  effectiveNoDivision: boolean
): CalendarUnit {
  if (effectiveNoDivision) {
    return unit;
  }
  return unit === 'month' || unit === 'year' ? unit : 'month';
}

function CalendarTimeRangeForm({
  onChange,
  noDivision = false,
  defaultIntervals,
}: CalendarTimeRangeProps) {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  const parsed = useMemo(() => {
    const result = parseDefaultIntervals(defaultIntervals);
    return result;
  }, [defaultIntervals]);
  const [start, setStart] = useState<Date | null>(
    parsed ? parsed.start : changeHourToStartOfDay(new Date())
  );
  const [end, setEnd] = useState<Date | null>(
    parsed ? parsed.end : changeHourToEndOfDay(new Date())
  );

  const initialDivideIntoSubintervals = useMemo(
    () => inferDivideIntoSubintervals(defaultIntervals),
    [defaultIntervals]
  );
  const [divideIntoSubintervals, setDivideIntoSubintervals] = useState(
    initialDivideIntoSubintervals
  );
  const effectiveNoDivision = noDivision || !divideIntoSubintervals;

  const [unit, setUnit] = useState<CalendarUnit>(
    normalizeUnitForCalendarMode(
      parsed ? parsed.unit : 'month',
      effectiveNoDivision
    )
  );

  const lastEmittedSignatureRef = useRef<string | null>(null);

  const divisionPreview = useMemo(() => {
    if (!start || !end) {
      return '';
    }
    return formatCalendarDivisionPreview({
      start,
      end,
      unit: normalizeUnitForCalendarMode(unit, effectiveNoDivision),
      divided: !effectiveNoDivision,
    });
  }, [start, end, unit, effectiveNoDivision]);

  const emitCalendarRangesIfChanged = useCallback(
    (
      source: 'dateRangeChange' | 'unitChange' | 'divisionChange',
      nextStart: Date,
      nextEnd: Date,
      nextUnit: CalendarUnit,
      nextEffectiveNoDivision: boolean
    ) => {
      const normalizedUnit = normalizeUnitForCalendarMode(
        nextUnit,
        nextEffectiveNoDivision
      );
      const signature = JSON.stringify({
        source,
        effectiveNoDivision: nextEffectiveNoDivision,
        startIso: nextStart.toISOString(),
        endIso: nextEnd.toISOString(),
        unit: normalizedUnit,
      });

      if (signature === lastEmittedSignatureRef.current) {
        return;
      }

      const formatterForNextUnit = new Intl.DateTimeFormat(
        undefined,
        normalizedUnit === 'month' ? { month: 'short' } : { year: 'numeric' }
      );

      const emitted = buildCalendarTimeRanges(
        changeHourToStartOfDay(nextStart),
        changeHourToEndOfDay(nextEnd),
        normalizedUnit,
        formatterForNextUnit,
        nextEffectiveNoDivision
      );

      lastEmittedSignatureRef.current = signature;
      onChange(emitted);
    },
    [onChange]
  );

  useEffect(() => {
    setDivideIntoSubintervals(initialDivideIntoSubintervals);
  }, [initialDivideIntoSubintervals]);

  const skipDivisionReEmitRef = useRef(true);
  useEffect(() => {
    if (skipDivisionReEmitRef.current) {
      skipDivisionReEmitRef.current = false;
      return;
    }
    if (!start || !end) {
      return;
    }
    emitCalendarRangesIfChanged(
      'divisionChange',
      start,
      end,
      unit,
      effectiveNoDivision
    );
  }, [effectiveNoDivision, emitCalendarRangesIfChanged, start, end, unit]);

  const showDivisionPreview = useMemo(() => {
    return !effectiveNoDivision && divisionPreview;
  }, [effectiveNoDivision, divisionPreview]);

  return (
    <Box className="nav-form-group">
      <FormControl sx={{ minWidth: '100%' }}>
        <DateTimeRangePickerComponent
          required
          format="DD/MM/YYYY"
          defaultValues={[start as Date, end as Date]}
          safeDefault={false}
          onChange={(value) => {
            if (!value) return;
            const [startDate, endDate] = value;
            if (startDate && endDate) {
              setStart(startDate);
              setEnd(endDate);
              emitCalendarRangesIfChanged(
                'dateRangeChange',
                startDate,
                endDate,
                unit,
                effectiveNoDivision
              );
            }
          }}
        />
      </FormControl>

      {!noDivision && (
        <Stack spacing={1} sx={{ mt: 1, marginBottom: '1.5em' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography variant="body2">Diviser en sous-intervalles</Typography>
            <Switch
              checked={divideIntoSubintervals}
              onChange={(_, checked) => {
                setDivideIntoSubintervals(checked);
              }}
            />
          </Stack>
          {showDivisionPreview && (
            <Typography variant="body2" color="text.secondary">
              {divisionPreview}
            </Typography>
          )}
        </Stack>
      )}

      {!noDivision && !effectiveNoDivision && (
        <FormControl sx={{ minWidth: '100%' }}>
          <InputLabel
            shrink
            id="unit-label"
            style={{
              zIndex: 10,
              backgroundColor: '#fff',
              paddingRight: '5px',
            }}
          >
            Divisée en *
          </InputLabel>
          <Select
            required
            labelId="unit-label"
            value={normalizeUnitForCalendarMode(unit, effectiveNoDivision)}
            label="Unit"
            onChange={(e) => {
              const newUnit = e.target.value as 'month' | 'year';
              setUnit(newUnit);
              if (start && end) {
                emitCalendarRangesIfChanged(
                  'unitChange',
                  start,
                  end,
                  newUnit,
                  effectiveNoDivision
                );
              }
            }}
          >
            <MenuItem value="month">Mois</MenuItem>
            <MenuItem value="year">Années</MenuItem>
          </Select>
        </FormControl>
      )}
    </Box>
  );
}

export default CalendarTimeRangeForm;
